import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const veedVideoBackgroundRemovalFastParams = z
  .object({
    subjectIsPerson: z.boolean().default(true),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const veedVideoBackgroundRemovalFast = defineModel({
  key: "veed-video-background-removal-fast",
  stepType: "video",
  spec: {
    label: "Remove BG - Cheap",
    description: "Fast background removal from videos with good quality",
    icon: "bg-remove.svg",
    tags: ["video-edit"],
    featured: false,
    info: "Generating a video with this model takes between 2-5 minutes.",
    features: {
      attachments: true,
      subjectIsPerson: true,
    } as const,
    options: {
      attachments: [
        {
          type: "video",
          allowedFormats: ["video/mp4", "video/webm", "video/quicktime"],
          label: "Video",
          slot: "video",
          required: true,
        },
      ],
      subjectIsPerson: {
        items: [
          { label: "Person", value: "true" },
          { label: "Object", value: "false" },
        ],
      },
    },
    params: veedVideoBackgroundRemovalFastParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/veed/video-background-removal/fast",
    entries: [{ unit: "per_second", unitPriceMicrodollars: 12_000 }],
  },
  providers: [
    {
      provider: "fal",
      modelId: "veed/video-background-removal/fast",
      estimateCost(_params) {
        // VEED Fast pricing: $0.012 per 30 frames with refine ON
        // Assuming 30fps, that's $0.012 per second
        // For a 10s video estimate: $0.12
        return 0.012 * 10 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      // $0.012 per 30 frames = $0.012 per second at 30fps
      costPerSecond: 0.012 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const getUrl = (slot: string) =>
          params.attachments?.find((attachment) => attachment.slot === slot)
            ?.data;

        const videoUrl = getUrl("video");

        if (!videoUrl) {
          throw new Error(
            "Video URL is required for VEED fast video background removal",
          );
        }

        return {
          model: this.modelId,
          prompt: "", // VEED doesn't use prompts, but FalVideoPayload requires it
          video_url: videoUrl,
          // Use vp9 for alpha channel support (transparent background)
          output_codec: "vp9",
          refine_foreground_edges: true,
          subject_is_person: params.subjectIsPerson,
        };
      },
    },
  ],
});
